/**
 * ShareDialog - Modal for creating ephemeral share links
 *
 * Supports sharing:
 * - Reports (from ReportsView)
 * - Vehicle dossiers (from VehicleDetailView)
 * - Claim packages (from ClaimDetailModal)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shareAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';

// labelKey/descKey resolved at render — never a translated string baked into a
// module-level constant, it would freeze at the language active on load.
const SHARE_TYPES = {
  report: {
    labelKey: 'shares.types.report.label',
    icon: '📊',
    descKey: 'shares.types.report.description'
  },
  vehicle: {
    labelKey: 'shares.types.vehicle.label',
    icon: '🚗',
    descKey: 'shares.types.vehicle.description'
  },
  claim: {
    labelKey: 'shares.types.claim.label',
    icon: '⚠️',
    descKey: 'shares.types.claim.description'
  }
};

const TTL_OPTIONS = [3, 7, 14, 30];

export default function ShareDialog({ isOpen, onClose, shareType, scope, title, onSuccess }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [ttlDays, setTtlDays] = useState(7);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [customTitle, setCustomTitle] = useState(title || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const typeInfo = SHARE_TYPES[shareType] || SHARE_TYPES.report;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await shareAPI.create({
        shareType,
        scope,
        title: customTitle || title,
        ttlDays,
        recipientEmail: recipientEmail || undefined
      });

      if (response.ok || response.shareId) {
        setResult(response);
        if (onSuccess) onSuccess(response);
      } else {
        setError(response.error?.message || t('shares.errors.createFailed'));
      }
    } catch (err) {
      setError(err.message || t('shares.errors.network'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (result?.token) {
      const url = shareAPI.getShareUrl(result.token);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyPin = async () => {
    if (result?.generatedPin) {
      await navigator.clipboard.writeText(result.generatedPin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Generate and download EML file with share link
  // RFC 5322 compliant - Outlook 365 compatible (no X-Unsent, never empty To:)
  const handleDownloadEML = () => {
    if (!result?.token) return;

    const shareUrl = shareAPI.getShareUrl(result.token);
    const subject = `[FleetManager] ${customTitle || title || 'Document partage'}`;
    const messageId = `<${Date.now()}.${Math.random().toString(36).substr(2, 9)}@fleetmanager.local>`;

    // Build From header with optional sender name
    const fromHeader = senderEmail
      ? (senderName ? `${senderName} <${senderEmail}>` : senderEmail)
      : 'FleetManager <noreply@fleetmanager.local>';

    // Build To header - use recipient email or fallback to undisclosed
    const toHeader = recipientEmail || 'undisclosed-recipients:;';

    // Body with CRLF line endings (RFC 5322 requirement)
    // Avoid special characters for 7bit encoding compatibility
    const senderSignature = senderName || 'FleetManager';
    const bodyLines = [
      'Bonjour,',
      '',
      'Je vous partage un document via FleetManager.',
      '',
      'Lien d\'acces securise :',
      shareUrl,
      '',
      'Ce lien est protege par un code PIN que je vous communiquerai separement.',
      '',
      `Ce lien expire le ${fmt.date(result.expiresAt)}.`,
      '',
      'Cordialement,',
      senderSignature
    ];
    const body = bodyLines.join('\r\n');

    // RFC 5322 compliant EML format - Outlook 365 compatible
    // Key fixes: No X-Unsent, never empty To:, includes Message-ID
    const emlContent = [
      'MIME-Version: 1.0',
      `Message-ID: ${messageId}`,
      `From: ${fromHeader}`,
      `To: ${toHeader}`,
      `Subject: ${subject}`,
      `Date: ${new Date().toUTCString()}`,
      'Content-Type: text/plain; charset=UTF-8',
      'Content-Transfer-Encoding: 7bit',
      '',
      body
    ].join('\r\n');

    const blob = new Blob([emlContent], { type: 'message/rfc822' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partage_${new Date().toISOString().split('T')[0]}.eml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{typeInfo.icon}</span>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                {t('shares.dialog.title', { label: t(typeInfo.labelKey) })}
              </h2>
              <p className="text-sm text-slate-500">{t(typeInfo.descKey)}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('shares.dialog.fields.title')}
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder={title || t('shares.dialog.titlePlaceholder', { label: t(typeInfo.labelKey) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* TTL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('shares.dialog.fields.ttl')}
                </label>
                <select
                  value={ttlDays}
                  onChange={(e) => setTtlDays(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TTL_OPTIONS.map(days => (
                    <option key={days} value={days}>
                      {days === 7
                        ? t('shares.ttl.recommended', { days })
                        : t('common.duration.days', { count: days })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email Configuration (for EML export) */}
              <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                  {t('shares.dialog.emailConfig.title')}
                </p>

                {/* Sender */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">{t('shares.dialog.emailConfig.senderName')}</label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Jean Dupont"
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">{t('shares.dialog.emailConfig.senderEmail')}</label>
                    <input
                      type="email"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      placeholder="jean.dupont@societe.fr"
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Recipient */}
                <div>
                  <label className="block text-xs text-slate-600 mb-1">{t('shares.dialog.emailConfig.recipientEmail')}</label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* PIN Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex gap-2">
                  <span className="text-amber-600">🔐</span>
                  <div className="text-sm text-amber-800">
                    <strong>{t('shares.dialog.pinNotice.title')}</strong>
                    <p className="mt-1 text-amber-700">
                      {t('shares.dialog.pinNotice.body')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('shares.dialog.creating')}
                    </>
                  ) : (
                    t('shares.dialog.createLink')
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Success State */
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 text-3xl mb-4">
                  ✓
                </div>
                <h3 className="text-lg font-semibold text-slate-800">
                  {t('shares.success.title')}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {t('shares.success.expiresOn', { date: fmt.date(result.expiresAt) })}
                </p>
              </div>

              {/* Share URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('shares.success.linkLabel')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareAPI.getShareUrl(result.token)}
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded-md text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                    title={t('shares.success.copyLinkTitle')}
                  >
                    {copied ? '✓' : '📋'}
                  </button>
                </div>
              </div>

              {/* Download Email Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleDownloadEML}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <span>📧</span>
                  {t('shares.success.downloadEmailButton')}
                </button>
                <p className="text-xs text-slate-500 text-center mt-2">
                  {t('shares.success.downloadEmailHint')}
                </p>
              </div>

              {/* PIN */}
              {result.generatedPin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('shares.success.pinLabel')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={result.generatedPin}
                      className="flex-1 px-3 py-2 bg-amber-50 border border-amber-300 rounded-md text-lg font-mono text-center tracking-widest"
                    />
                    <button
                      type="button"
                      onClick={handleCopyPin}
                      className="px-3 py-2 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors"
                      title={t('shares.success.copyPinTitle')}
                    >
                      📋
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-amber-600">
                    {t('shares.success.pinWarning')}
                  </p>
                </div>
              )}

              {/* Artifacts */}
              {result.artifacts && result.artifacts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('shares.success.artifactsLabel')}
                  </label>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {result.artifacts.map((a, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-slate-400">
                          {a.kind === 'pdf' ? '📄' : a.kind === 'csv' ? '📊' : '📎'}
                        </span>
                        {a.name}.{a.mimeType?.split('/')[1] || a.kind}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Close button */}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                >
                  {t('common.actions.close')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
